/* Native category links remain available when this entrance is not enhanced. */
(() => {
  if (customElements.get('nd-store-entrance')) return;
  const visitKey = 'noel-drew-boutique-entered-v1';
  const fadeKey = 'nd-page-fade';
  const foamSegments = [[1, 5], [8, 11], [14, 19], [23, 27], [31, 35]];

  class JewelryBoxEntrance extends HTMLElement {
    connectedCallback() {
      this.content = document.getElementById('StoreContent');
      if (!this.content) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.compactEffects = window.matchMedia('(max-width: 760px), (pointer: coarse)').matches;
      // Focusing the scene after a return must not outline its entire image on iOS.
      this.tabIndex = -1;
      this.style.outline = 'none';
      this.openButton = this.querySelector('[data-box-open]');
      this.collections = this.querySelector('.nd-box-collections');
      this.shopControls = this.querySelector('.nd-box-shop-controls');
      this.help = this.querySelector('.nd-box-help');
      this.status = this.querySelector('.nd-live');
      this.originalHelp = this.help?.textContent || '';
      this.motionPaused = false;
      this.classList.add('is-enhanced');
      this.classList.toggle('nd-compact-effects', this.compactEffects);
      if (this.openButton) this.openButton.hidden = false;
      this.resetBox();

      let hasEntered = false;
      try { hasEntered = sessionStorage.getItem(visitKey) === 'yes'; } catch (_) { /* Storage is optional. */ }
      const force = new URLSearchParams(window.location.search).get('entrance') === '1';
      const deepLink = Boolean(window.location.hash && window.location.hash !== '#shop-entrance');
      const skip = !window.Shopify?.designMode && !force &&
        (deepLink || (this.dataset.once === 'true' && hasEntered));
      this.hidden = skip;
      this.content.hidden = !skip;
      this.storeReady = window.NoelDrewShop?.readyMain();

      this.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : event.target?.parentElement;
        if (!target || event.defaultPrevented || this.hidden) return;
        const opener = target.closest('[data-box-open]');
        if (opener) {
          event.preventDefault();
          if (this.state === 'open') this.resetBox();
          else this.open();
          return;
        }
        const shop = target.closest('[data-box-shop], [data-enter], [data-enter-instant]');
        if (shop) {
          event.preventDefault();
          this.enter(shop.hasAttribute('data-enter-instant'));
          return;
        }
        const destination = target.closest('a[data-box-destination]');
        if (!destination || event.button !== 0 || event.metaKey || event.ctrlKey ||
            event.shiftKey || event.altKey || destination.target === '_blank' ||
            destination.hasAttribute('download')) return;
        if (this.state !== 'open') {
          event.preventDefault();
          return;
        }
        const url = new URL(destination.href, window.location.href);
        if (url.origin !== window.location.origin) return;
        // Prevent the document's regular soft-navigation handler from racing the light.
        event.preventDefault();
        this.leave(url, false, destination.getAttribute('aria-label') || destination.textContent.trim());
      }, options);

      document.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : event.target?.parentElement;
        if (!target || event.defaultPrevented) return;
        if (!this.hidden && target.closest('a.skip[href="#main"]')) {
          event.preventDefault();
          this.enter(true);
          return;
        }
        const revisit = target.closest('[data-open-entrance]');
        if (!revisit || event.button !== 0 || event.metaKey || event.ctrlKey ||
            event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.reopen();
      }, options);

      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        if (this.revealVeil) {
          event.preventDefault();
          this.clearArrival();
        } else if (!this.hidden) {
          event.preventDefault();
          if (this.state === 'approaching' || this.state === 'opening') this.completeOpen();
          else if (this.state === 'leaving') this.cancelLeaving();
          else this.enter(true);
        }
      }, options);
      document.addEventListener('shopify:section:select', event => {
        if (event.target instanceof Element && event.target.contains(this)) this.reopen();
      }, options);
      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) {
          if (this.state === 'approaching' || this.state === 'opening') this.completeOpen();
          else if (this.state === 'leaving') this.finish();
          this.clearArrival();
        }
        this.syncMotion();
      }, options);
      document.addEventListener('visibilitychange', () => this.syncMotion(), options);
      window.addEventListener('pageshow', event => {
        if (!event.persisted) return;
        this.stopBoxWake();
        this.clearArrival();
        if (this.state === 'leaving') this.cancelLeaving();
        else if (this.state === 'approaching' || this.state === 'opening') this.completeOpen();
        this.resizeRiver();
        this.syncMotion();
      }, options);
      this.setupRiver(options);
    }

    setChoices(available) {
      if (this.collections) {
        this.collections.inert = !available;
        this.collections.setAttribute('aria-hidden', String(!available));
      }
      // Also cover any flat category links placed outside the tray navigation.
      this.querySelectorAll('[data-box-destination]').forEach(link => {
        if (!link.hasAttribute('data-box-tabindex')) {
          link.dataset.boxTabindex = link.getAttribute('tabindex') ?? '';
        }
        if (available) {
          const value = link.dataset.boxTabindex;
          if (value === '') link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', value);
          link.removeAttribute('aria-disabled');
        } else {
          link.setAttribute('tabindex', '-1');
          link.setAttribute('aria-disabled', 'true');
        }
      });
      if (this.shopControls) this.shopControls.hidden = !available;
    }

    resetBox() {
      this.stopBoxWake();
      clearTimeout(this.approachTimer);
      clearTimeout(this.openTimer);
      clearTimeout(this.finishTimer);
      clearTimeout(this.navigationTimer);
      this.approachTimer = this.openTimer = this.finishTimer = this.navigationTimer = 0;
      this.pendingDestination = null;
      this.entering = false;
      this.state = 'closed';
      this.dataset.boxState = this.state;
      this.classList.remove('is-box-approaching', 'is-box-opening', 'is-box-open', 'is-leaving');
      this.removeAttribute('aria-busy');
      this.setChoices(false);
      if (this.openButton) {
        this.openButton.disabled = false;
        this.openButton.setAttribute('aria-expanded', 'false');
        this.openButton.setAttribute('aria-label', 'Open the Noel Drew jewelry box');
      }
      if (this.help) this.help.textContent = this.originalHelp;
      if (this.status) this.status.textContent = '';
    }

    open() {
      if (this.hidden || this.state !== 'closed') return;
      window.NoelDrewShop?.warmCollections();
      this.state = 'approaching';
      this.dataset.boxState = this.state;
      this.classList.add('is-box-approaching');
      this.startBoxWake();
      if (this.openButton) this.openButton.setAttribute('aria-expanded', 'true');
      if (this.status) this.status.textContent = 'Moving closer to the jewelry box.';
      if (this.help) this.help.textContent = 'A closer look…';
      if (this.reducedMotion.matches) this.completeOpen();
      else this.approachTimer = setTimeout(() => this.beginLidOpening(), 950);
    }

    beginLidOpening() {
      clearTimeout(this.approachTimer);
      this.approachTimer = 0;
      if (this.hidden || this.state !== 'approaching') return;
      this.state = 'opening';
      this.dataset.boxState = this.state;
      this.classList.add('is-box-opening');
      if (this.status) this.status.textContent = 'Opening the jewelry box.';
      if (this.help) this.help.textContent = 'Opening the jewelry box…';
      this.openTimer = setTimeout(() => this.completeOpen(), 2200);
    }

    completeOpen() {
      clearTimeout(this.approachTimer);
      clearTimeout(this.openTimer);
      this.approachTimer = this.openTimer = 0;
      if (this.hidden || this.state === 'leaving') return;
      this.state = 'open';
      this.dataset.boxState = this.state;
      this.classList.add('is-box-open');
      this.classList.remove('is-box-opening');
      this.setChoices(true);
      if (this.openButton) {
        this.openButton.disabled = false;
        this.openButton.setAttribute('aria-expanded', 'true');
        this.openButton.setAttribute('aria-label', 'Close the Noel Drew jewelry box');
      }
      if (this.help) this.help.textContent = 'Choose a piece to explore its collection.';
      if (this.status) this.status.textContent = 'The jewelry box is open. Choose Fine Jewelry, Hammered, or Custom.';
    }

    enter(instant = false) {
      const home = new URL(document.body.dataset.shopRoot || '/', location.origin);
      home.hash = 'worlds';
      this.leave(this.content.querySelector('.nd-home-catalog') ? null : home, instant);
    }

    leave(destination, instant = false, label = '') {
      if (this.hidden || (this.state === 'leaving' && !instant)) return;
      this.stopBoxWake();
      clearTimeout(this.approachTimer);
      clearTimeout(this.openTimer);
      clearTimeout(this.finishTimer);
      clearTimeout(this.navigationTimer);
      this.pendingDestination = destination;
      this.destinationReady = destination && window.NoelDrewShop?.canHandle(destination)
        ? window.NoelDrewShop.prepare(destination) : null;
      this.entering = true;
      this.state = 'leaving';
      this.dataset.boxState = this.state;
      this.setAttribute('aria-busy', 'true');
      this.setChoices(false);
      if (this.openButton) this.openButton.disabled = true;
      if (this.help) this.help.textContent = 'Entering the boutique…';
      if (this.status) this.status.textContent = label ? 'Entering ' + label + '.' : 'Entering the boutique.';
      if (instant || this.reducedMotion.matches) {
        this.finish();
        return;
      }
      this.classList.add('is-leaving');
      this.finishTimer = setTimeout(() => this.finish(true), 1400);
    }

    async finish(withGlow = false) {
      this.stopBoxWake();
      clearTimeout(this.approachTimer);
      clearTimeout(this.openTimer);
      clearTimeout(this.finishTimer);
      clearTimeout(this.navigationTimer);
      const destination = this.pendingDestination;
      if (!destination) {
        await this.storeReady;
        if (this.state !== 'leaving') return;
      }
      try { sessionStorage.setItem(visitKey, 'yes'); } catch (_) { /* Shopping never depends on storage. */ }
      if (destination && window.NoelDrewShop?.canHandle(destination)) {
        await this.destinationReady;
        if (this.state !== 'leaving' || this.pendingDestination !== destination) return;
        const mounted = await window.NoelDrewShop.mount(destination);
        if (this.state !== 'leaving') return;
        if (mounted) this.pendingDestination = null;
        else {
          try { sessionStorage.setItem(fadeKey, JSON.stringify({time:Date.now(),path:destination.pathname + destination.search,entrance:true})); } catch (_) {}
          window.location.assign(destination.href);
          this.navigationTimer = setTimeout(() => this.cancelLeaving(),8000);
          return;
        }
      } else if (destination) {
        try {
          if (withGlow && !this.reducedMotion.matches) {
            sessionStorage.setItem(fadeKey, JSON.stringify({ time: Date.now(), path: destination.pathname + destination.search, entrance: true }));
          } else sessionStorage.removeItem(fadeKey);
        } catch (_) { /* Native navigation still works without storage. */ }
        window.location.assign(destination.href);
        // A cancelled or failed navigation must not strand an opaque entrance.
        this.navigationTimer = setTimeout(() => this.cancelLeaving(), 8000);
        return;
      }
      this.clearArrival();
      if (withGlow && !this.reducedMotion.matches) {
        this.revealVeil = document.createElement('div');
        this.revealVeil.className = 'nd-arrival-glow';
        this.revealVeil.setAttribute('aria-hidden', 'true');
        document.body.append(this.revealVeil);
        this.content.classList.add('nd-shop-arriving');
        this.content.inert = true;
        this.revealTimer = setTimeout(() => this.clearArrival(), 2850);
      }
      this.content.hidden = false;
      this.hidden = true;
      const shopUrl = new URL(location.href);
      if (shopUrl.searchParams.has('entrance')) {
        shopUrl.searchParams.delete('entrance');
        shopUrl.hash = 'worlds';
        history.replaceState(history.state, '', shopUrl);
      }
      this.resetBox();
      this.syncMotion();
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.getElementById('main')?.focus({ preventScroll: true });
    }

    cancelLeaving() {
      clearTimeout(this.finishTimer);
      clearTimeout(this.navigationTimer);
      this.finishTimer = this.navigationTimer = 0;
      this.pendingDestination = null;
      this.destinationReady = null;
      this.entering = false;
      this.state = 'opening';
      this.classList.remove('is-leaving');
      this.removeAttribute('aria-busy');
      try { sessionStorage.removeItem(fadeKey); } catch (_) { /* Storage is optional. */ }
      this.completeOpen();
      this.syncMotion();
    }

    reopen() {
      this.clearArrival();
      this.resetBox();
      this.hidden = false;
      this.content.hidden = true;
      this.resizeRiver();
      this.syncMotion();
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      this.focus({ preventScroll: true });
    }

    clearArrival() {
      clearTimeout(this.revealTimer);
      this.revealTimer = 0;
      this.revealVeil?.remove();
      this.revealVeil = null;
      this.content?.classList.remove('nd-shop-arriving');
      if (this.content) this.content.inert = false;
    }

    setupRiver(options) {
      this.riverCanvas = this.querySelector('.nd-box-river-canvas');
      this.riverImage = this.querySelector('.nd-river-image');
      this.motionButton = this.querySelector('.nd-box-motion');
      this.riverContext = this.riverCanvas?.getContext('2d', { alpha: false });
      this.wakeCanvas = this.querySelector('.nd-box-wake-canvas');
      this.wakeContext = this.wakeCanvas?.getContext('2d');
      this.boxFront = this.querySelector('.nd-box-front');
      this.waterSurfaceNodes = [...this.querySelectorAll('.nd-box-reflection,.nd-box-waterline')];
      this.waterSurfaceStyles = null;
      this.riverTime = 0;
      this.riverLastFrame = 0;
      this.motionButton?.addEventListener('click', () => {
        this.motionPaused = !this.motionPaused;
        this.syncMotion();
      }, options);
      this.riverImage?.addEventListener('load', () => {
        this.resizeRiver();
        this.syncMotion();
      }, options);
      this.riverResize?.disconnect();
      const surface = this.querySelector('.nd-box-river-water');
      if (surface && this.riverContext && window.ResizeObserver) {
        this.riverResize = new ResizeObserver(() => this.resizeRiver());
        this.riverResize.observe(surface);
      }
      this.riverTick = now => {
        this.riverFrame = 0;
        if (this.hidden || document.hidden || this.motionPaused || this.reducedMotion.matches) return;
        const elapsed = now - this.riverLastFrame;
        if (elapsed >= 1000 / 24) {
          this.riverTime += Math.min(elapsed, 65) / 1000;
          this.riverLastFrame = now;
          this.drawRiver();
          if (this.wakeActive) this.drawBoxWake(now);
        }
        if (this.riverReady() || this.wakeActive) this.riverFrame = requestAnimationFrame(this.riverTick);
      };
      this.resizeRiver();
      this.syncMotion();
    }

    resizeRiver() {
      this.resizeBoxWake();
      if (!this.riverContext || this.hidden) return;
      const rect = this.riverCanvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const scale = Math.min(window.devicePixelRatio || 1, this.compactEffects ? 1 : 1.5,
        (this.compactEffects ? 640 : 1600) / rect.width);
      this.riverCssWidth = rect.width;
      const width = Math.max(1, Math.round(rect.width * scale));
      const height = Math.max(1, Math.round(rect.height * scale));
      if (this.riverCanvas.width !== width) this.riverCanvas.width = width;
      if (this.riverCanvas.height !== height) this.riverCanvas.height = height;
      this.drawRiver();
    }

    waterSample(depth, lateral = 0) {
      const d = Math.max(0, Math.min(1.2, depth));
      const time = this.riverTime || 0;
      const perspective = .14 + .86 * d ** 1.15;
      // Longer swells advance faster than small ripples, all toward the foreground.
      const swell = d * 10.9 + lateral * .9 - time * .43;
      const ripple = d * 27.7 - lateral * 3.1 - time * .69;
      const detail = d * 63.1 + lateral * 7.3 - time * 1.21;
      return {
        x: (Math.sin(swell) * 4.7 + Math.sin(ripple) * 1.55 + Math.sin(detail) * .42) * perspective,
        y: (Math.cos(swell) * 1.3 + Math.sin(ripple + .8) * .48 + Math.cos(detail + .7) * .18) * perspective,
        crest: .5 + .5 * (Math.sin(ripple + .6) * .55 + Math.sin(detail + .1) * .3 + Math.sin(swell) * .15)
      };
    }

    drawRiver() {
      const image = this.riverImage;
      if (!this.riverContext || !image?.complete || !image.naturalWidth) return;
      const { width, height } = this.riverCanvas;
      if (!width || !height) return;
      const overscanX = 28;
      const overscanY = 12;
      const ratio = Math.max((width + overscanX * 2) / image.naturalWidth,
        (height + overscanY * 2) / image.naturalHeight);
      const sourceWidth = (width + overscanX * 2) / ratio;
      const sourceHeight = (height + overscanY * 2) / ratio;
      const sourceX = (image.naturalWidth - sourceWidth) / 2;
      const sourceY = (image.naturalHeight - sourceHeight) / 2;
      const pixelScale = width / (this.riverCssWidth || width);
      // Small horizontal and vertical refraction avoids a whole image swaying sideways.
      const strip = this.compactEffects ? 8 : 4;
      for (let y = 0; y < height; y += strip) {
        const wave = this.waterSample(y / height);
        const band = Math.min(strip, height - y);
        this.riverContext.drawImage(image, sourceX, sourceY + (y + overscanY + wave.y * pixelScale) / ratio,
          sourceWidth, band / ratio, -overscanX + wave.x * pixelScale, y, width + overscanX * 2, band);
      }
      this.syncWaterSurface();
      this.classList.add('has-river-canvas');
    }

    syncWaterSurface() {
      if (!this.waterSurfaceNodes?.length) return;
      const wave = this.waterSample(this.waterContactDepth ?? .58);
      const values = [(wave.x * .34).toFixed(2) + 'px', (wave.y * .32).toFixed(2) + 'px', (.96 + wave.crest * .08).toFixed(3)];
      const properties = ['--nd-water-drift-x', '--nd-water-drift-y', '--nd-water-sheen'];
      values.forEach((value, index) => {
        if (value === this.waterSurfaceStyles?.[index]) return;
        // Local surface variables never invalidate the box's independent 3D faces.
        this.waterSurfaceNodes.forEach(node => node.style.setProperty(properties[index], value));
      });
      this.waterSurfaceStyles = values;
    }

    riverReady() {
      return Boolean(this.riverContext && this.riverImage?.complete && this.riverImage.naturalWidth > 0);
    }

    resizeBoxWake() {
      if (!this.wakeContext || this.hidden) return;
      const bounds = this.wakeCanvas.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      const scale = Math.min(window.devicePixelRatio || 1, this.compactEffects ? 1 : 1.5,
        (this.compactEffects ? 640 : 1600) / bounds.width);
      const width = Math.max(1, Math.round(bounds.width * scale));
      const height = Math.max(1, Math.round(bounds.height * scale));
      // Resizing clears the bitmap, so only allocate when the viewport changes.
      if (this.wakeCanvas.width !== width) this.wakeCanvas.width = width;
      if (this.wakeCanvas.height !== height) this.wakeCanvas.height = height;
      this.wakeBounds = { left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height };
      this.wakeWaterBounds = this.riverCanvas?.getBoundingClientRect();
      this.wakePreviousContact = null;
    }

    boxWaterContact() {
      if (!this.boxFront || !this.wakeBounds) return null;
      // Measure the physical front, rather than estimating its CSS perspective.
      // Geometry reads happen only during this short splash, never while idle.
      const face = this.boxFront.getBoundingClientRect();
      if (!face.width || !face.height) return null;
      const width = face.width * .98;
      const center = (face.left + face.right) * .5 - this.wakeBounds.left;
      const unit = Math.max(.35, Math.min(1.8, width / 360));
      const water = this.wakeWaterBounds;
      const depth = water?.height ? (face.bottom - water.top) / water.height : .58;
      this.waterContactDepth = depth;
      const wave = this.waterSample(depth);
      return {
        left: center - width * .5, right: center + width * .5, center, width, unit,
        y: face.bottom - this.wakeBounds.top - face.height * .025 + wave.y * .38
      };
    }

    startBoxWake() {
      this.stopBoxWake();
      if (!this.wakeContext || this.motionPaused || this.reducedMotion.matches || this.hidden || document.hidden) return;
      this.resizeBoxWake();
      if (!this.boxWaterContact()) return;
      this.wakeActive = true;
      this.wakeStarted = performance.now();
      this.wakeLastDraw = this.wakeStarted;
      this.wakeEnergy = 0;
      this.wakeSprayBudget = 0;
      this.wakeSerial = 0;
      this.wakeDrops = [];
      this.wakeTrail = [];
      this.syncMotion();
    }

    stopBoxWake() {
      this.wakeActive = false;
      this.wakeStarted = 0;
      this.wakeEnergy = 0;
      this.wakeSprayBudget = 0;
      this.wakePreviousContact = null;
      this.wakeDrops = [];
      this.wakeTrail = [];
      if (this.wakeContext && this.wakeCanvas) {
        this.wakeContext.setTransform(1, 0, 0, 1, 0, 0);
        this.wakeContext.clearRect(0, 0, this.wakeCanvas.width, this.wakeCanvas.height);
      }
    }

    emitBoxSpray(now, contact, force, amount = 4) {
      const seed = number => {
        const n = Math.sin(number * 127.1 + 311.7) * 43758.5453;
        return n - Math.floor(n);
      };
      for (let count = 0; count < amount; count++) {
        const serial = this.wakeSerial++;
        const side = serial % 2 ? 1 : -1;
        this.wakeDrops.push({
          born: now, x: contact.center + side * contact.width * (.43 + seed(serial) * .085),
          y: contact.y + (seed(serial + 5) - .5) * 4 * contact.unit,
          vx: side * (18 + force * 56 + seed(serial + 10) * 36) * contact.unit,
          vy: -(32 + force * 92 + seed(serial + 20) * 68) * contact.unit,
          gravity: (265 + seed(serial + 30) * 85) * contact.unit,
          radius: (.4 + seed(serial + 40) * .85) * contact.unit,
          life: .48 + seed(serial + 50) * .42
        });
      }
      const limit = this.compactEffects ? 56 : 96;
      if (this.wakeDrops.length > limit) this.wakeDrops.splice(0, this.wakeDrops.length - limit);
    }

    drawBoxWake(now) {
      if (!this.wakeActive) return;
      const elapsed = now - this.wakeStarted;
      // The pull lasts 950 ms; its displaced water settles during the next 1.4 s.
      if (elapsed > 2350 || this.hidden || document.hidden || this.motionPaused || this.reducedMotion.matches) {
        this.stopBoxWake();
        return;
      }
      const contact = this.boxWaterContact();
      if (!contact) { this.stopBoxWake(); return; }
      const ctx = this.wakeContext;
      const bounds = this.wakeBounds;
      ctx.setTransform(this.wakeCanvas.width / bounds.width, 0, 0, this.wakeCanvas.height / bounds.height, 0, 0);
      ctx.clearRect(0, 0, bounds.width, bounds.height);
      const dt = Math.max(.016, Math.min(.1, (now - this.wakeLastDraw) / 1000));
      const velocity = this.wakePreviousContact ? Math.abs(contact.y - this.wakePreviousContact.y) / dt : 0;
      const unit = contact.unit;
      const movement = elapsed < 1050 ? Math.min(1, Math.max(0, velocity - 3 * unit) / (contact.width * .25)) : 0;
      const response = movement > this.wakeEnergy ? .12 : .35;
      this.wakeEnergy += (movement - this.wakeEnergy) * (1 - Math.exp(-dt / response));
      const settle = Math.max(0, Math.min(1, (2350 - elapsed) / 550));
      // Momentum builds from the measured pull and drains away without an opacity pulse.
      const force = this.wakeEnergy * settle * settle * (3 - 2 * settle);
      this.wakeLastDraw = now;
      this.wakePreviousContact = contact;
      this.wakeTrail.push({ y: contact.y, born: now });
      const trailLimit = this.compactEffects ? 20 : 30;
      this.wakeTrail = this.wakeTrail.filter(point => now - point.born < 1300).slice(-trailLimit);

      const waterTop = this.wakeWaterBounds ? this.wakeWaterBounds.top - bounds.top : 0;
      const waterHeight = this.wakeWaterBounds?.height || bounds.height;
      const points = this.wakeTrail.slice().reverse().map(point => ({
        ...point, age: (now - point.born) / 1300,
        wave: this.waterSample((point.y - waterTop) / waterHeight)
      }));
      // Broken side wakes travel away from the box, leaving the wood unobscured.
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (const side of [-1, 1]) {
        for (let filament = 0; filament < 3; filament++) {
          ctx.beginPath();
          points.forEach((point, index) => {
            const age = point.age;
            const spread = contact.width * (.012 + age * .15 + filament * .012);
            const x = contact.center + side * (contact.width * .5 + spread)
              + point.wave.x * .4 + Math.sin(age * 9.7 + filament * 1.9) * .6 * unit;
            const y = point.y + (4 + filament * 3 + age * 7) * unit + point.wave.y * .5;
            if (index === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          });
          const sheen = ctx.createLinearGradient(0, contact.y - contact.width * .28, 0, contact.y + 12 * unit);
          sheen.addColorStop(0, 'rgba(255,238,193,0)');
          sheen.addColorStop(.65, 'rgba(239,220,171,' + force * .15 + ')');
          sheen.addColorStop(1, 'rgba(255,247,219,' + force * (.46 - filament * .09) + ')');
          ctx.strokeStyle = sheen;
          ctx.lineWidth = (2.8 - filament * .7) * unit;
          ctx.stroke();
        }
      }

      const crest = [];
      const lift = contact.width * .09 * force;
      for (let step = 0; step <= 36; step++) {
        const f = step / 36;
        const envelope = Math.sin(f * Math.PI) ** .7;
        const wave = this.waterSample((contact.y - waterTop) / waterHeight, f - .5);
        const ripple = .78 + .22 * wave.crest;
        crest.push({
          x: contact.left + contact.width * f,
          y: contact.y + contact.width * .012 * envelope - lift * envelope * ripple + wave.y * .7
        });
      }
      // A thin refractive sheet rolls over itself along the moving front edge.
      ctx.save();
      ctx.beginPath();
      crest.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y));
      for (let step = crest.length - 1; step >= 0; step--) {
        const envelope = Math.sin(step / 36 * Math.PI);
        ctx.lineTo(crest[step].x, crest[step].y + (3 + envelope * (5 + force * 12)) * unit);
      }
      ctx.closePath();
      const sheet = ctx.createLinearGradient(0, contact.y - lift, 0, contact.y + 28 * unit);
      sheet.addColorStop(0, 'rgba(255,248,222,' + force * .5 + ')');
      sheet.addColorStop(.35, 'rgba(238,219,176,' + force * .22 + ')');
      sheet.addColorStop(.68, 'rgba(97,82,53,' + force * .15 + ')');
      sheet.addColorStop(1, 'rgba(185,153,95,0)');
      ctx.fillStyle = sheet;
      ctx.fill();
      const water = this.wakeWaterBounds;
      if (this.riverReady() && water) {
        ctx.clip();
        ctx.globalAlpha = force * .18;
        ctx.drawImage(this.riverCanvas, water.left - bounds.left, water.top - bounds.top - lift * .3, water.width, water.height);
      }
      ctx.restore();
      // Short interrupted highlights avoid a solid white line around the box.
      for (const [start, end] of foamSegments) {
        ctx.beginPath();
        for (let index = start; index <= end; index++) {
          const p = crest[index];
          if (index === start) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
        }
        const sheen = this.waterSample((contact.y - waterTop) / waterHeight, start / 36 - .5).crest;
        ctx.strokeStyle = 'rgba(255,249,225,' + force * (.29 + .18 * sheen) + ')';
        ctx.lineWidth = (1.1 + force * .7) * unit;
        ctx.stroke();
      }

      // Two low curls sweep outward at the leading corners before falling back.
      for (const side of [-1, 1]) {
        const x = side < 0 ? contact.left : contact.right;
        const curlField = this.waterSample((contact.y - waterTop) / waterHeight, side * .5);
        const curl = contact.width * .12 * force * (.86 + .14 * curlField.crest);
        for (let thread = 0; thread < 2; thread++) {
          const height = curl * (.74 + thread * .24);
          const reach = contact.width * (.075 + thread * .025) * force;
          ctx.beginPath();
          ctx.moveTo(x - side * 5 * unit, contact.y + 4 * unit);
          ctx.bezierCurveTo(x + side * reach * .1, contact.y - height,
            x + side * reach * .72, contact.y - height * .85,
            x + side * reach, contact.y + (6 + thread * 3) * unit);
          const curlSheen = ctx.createLinearGradient(0, contact.y - height, 0, contact.y + 10 * unit);
          curlSheen.addColorStop(0, 'rgba(255,246,216,' + force * .5 + ')');
          curlSheen.addColorStop(.42, 'rgba(225,205,160,' + force * .25 + ')');
          curlSheen.addColorStop(1, 'rgba(154,130,83,0)');
          ctx.strokeStyle = curlSheen;
          ctx.lineWidth = (3.3 - thread * .9) * unit;
          ctx.stroke();
        }
      }

      if (elapsed < 1050 && force > .13) {
        this.wakeSprayBudget += force * dt * (this.compactEffects ? 30 : 40);
        const amount = Math.min(4, Math.floor(this.wakeSprayBudget));
        if (amount >= 2) {
          this.emitBoxSpray(now, contact, force, amount);
          this.wakeSprayBudget -= amount;
        }
      }
      this.wakeDrops = this.wakeDrops.filter(drop => now - drop.born < drop.life * 1000);
      for (const drop of this.wakeDrops) {
        const age = (now - drop.born) / 1000;
        const x = drop.x + drop.vx * age;
        const y = drop.y + drop.vy * age + drop.gravity * age * age * .5;
        if (y > drop.y + 8 * unit) continue;
        const fade = Math.min(1, age * 20) * Math.max(0, 1 - age / drop.life);
        const vertical = drop.vy + drop.gravity * age;
        ctx.beginPath();
        ctx.ellipse(x, y, drop.radius, drop.radius * (1.15 + Math.min(1.4, Math.abs(vertical) / 120)),
          -Math.atan2(drop.vx, 100) * .35, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(231,220,189,' + fade * .58 + ')';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x - drop.radius * .2, y - drop.radius * .35, drop.radius * .3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,251,230,' + fade * .72 + ')';
        ctx.fill();
      }
    }

    syncMotion() {
      cancelAnimationFrame(this.riverFrame);
      this.riverFrame = 0;
      const paused = this.motionPaused || this.reducedMotion.matches;
      if (paused || this.hidden || document.hidden) this.stopBoxWake();
      this.classList.toggle('nd-motion-paused', paused || this.hidden || document.hidden);
      if (this.motionButton) {
        this.motionButton.hidden = this.reducedMotion.matches;
        this.motionButton.setAttribute('aria-label', this.motionPaused ? 'Play ambient motion' : 'Pause ambient motion');
        this.motionButton.setAttribute('aria-pressed', String(this.motionPaused));
      }
      const ready = this.riverReady() || this.wakeActive;
      const running = ready && !paused && !this.hidden && !document.hidden;
      this.dataset.riverState = running ? 'flowing' : 'still';
      if (running) {
        this.riverLastFrame = performance.now();
        this.riverFrame = requestAnimationFrame(this.riverTick);
      }
    }

    disconnectedCallback() {
      this.stopBoxWake();
      clearTimeout(this.approachTimer);
      clearTimeout(this.openTimer);
      clearTimeout(this.finishTimer);
      clearTimeout(this.navigationTimer);
      this.clearArrival();
      cancelAnimationFrame(this.riverFrame);
      this.riverResize?.disconnect();
      this.controller?.abort();
      if (this.content) this.content.hidden = false;
    }
  }

  customElements.define('nd-store-entrance', JewelryBoxEntrance);
})();
